import { Component, type FC, type ReactElement } from "react";
import { useSelector } from "react-redux";

import { Collapse, TimeAgo } from "widgets";

import "./styles/_errors-page.scss";

import styleLayout from "./styles/PageLayout.module.scss";

import { type IStampedError } from "../../redux/modules/errors.js";
import { type IAppState } from "../../redux/reducer.js";
import { ContentPlaceholder } from "./ContentPlaceholder.js";
import PageHeader from "./navigation-panel/PageHeader.js";

export interface IErrorDetails {
  request?: string;
  variables?: string;
  stacktrace: string;
}

function getGraphQLErrorDetails(error: any): IErrorDetails {
  let stacktrace: string = null;

  // try to build the stack trace out of the rootCause
  if (error.rootCause && (error.rootCause.cause || error.rootCause.stackTrace)) {
    const stackTraceArray = error.rootCause.cause ? error.rootCause.cause.stackTrace : error.rootCause.stackTrace;
    stacktrace = convertJavaExceptionStacktraceToString(stackTraceArray);
  }

  return {
    request: error.request,
    variables: JSON.stringify(error.variables, null, 2), // spacing level = 2
    stacktrace: stacktrace,
  };
}

function convertJavaExceptionStacktraceToString(stacktrace: any[]) {
  if (!stacktrace) {
    return null;
  }
  let message = "";
  stacktrace.map((trace: any) => {
    message += trace.fileName + " line " + trace.lineNumber + " : " + trace.className + "." + trace.methodName + "\n";
  });
  return message;
}

function getMTExceptionDetails(error: any): IErrorDetails {
  const stacktrace: string = convertJavaExceptionStacktraceToString(error.stacktrace);

  return {
    stacktrace: stacktrace,
  };
}

export function getErrorStack(error: any): string {
  if (error?.stack) {
    return error.stack;
  }
  return null;
}

export function getErrorDetails(stampedError: IStampedError): IErrorDetails {
  if (!stampedError.error) {
    return null;
  }

  if (stampedError.error.graphQLErrors && stampedError.error.graphQLErrors.length > 0) {
    // graphQL exceptions from the middleware have special format, no way to get the stack trace via "error.stack"
    return getGraphQLErrorDetails(stampedError.error.graphQLErrors[0]);
  } else if (stampedError.error.networkError?.stack) {
    // network errors
    return { stacktrace: getErrorStack(stampedError.error.networkError) };
  } else if (stampedError.error.stacktrace) {
    return getMTExceptionDetails(stampedError.error);
  } else if (stampedError.error.stack) {
    // other standard client-side errors
    return { stacktrace: getErrorStack(stampedError.error) };
  }
  // e.g. graphQl errors (as opposed to exceptions) have no more details
  return null;
}

export function getErrorMessage(stampedError: IStampedError): string {
  if (!stampedError.error) {
    return "No information";
  }
  // special case for graphQl errors since the root message is the concatenation of all graphql error messages,
  // and that leads to often the same message repeated X times
  if (stampedError.error.graphQLErrors && stampedError.error.graphQLErrors.length > 0) {
    return stampedError.error.graphQLErrors[0].message + " (message 1/" + stampedError.error.graphQLErrors.length + ")";
  }
  return stampedError.error.message;
}

interface IStampedErrorState {
  openStack: boolean;
  openQueryInfo: boolean;
}

class ErrorComponent extends Component<IStampedError, IStampedErrorState> {
  constructor(props: IStampedError) {
    super(props);

    this.state = {
      openStack: false,
      openQueryInfo: false,
    };
  }

  render(): ReactElement {
    const errorMessage: string = getErrorMessage(this.props);
    const errorDetails: IErrorDetails = getErrorDetails(this.props);

    let stackTrace: ReactElement = null;
    if (errorDetails && errorDetails.stacktrace) {
      stackTrace = (
        <div>
          <a onClick={() => this.setState({ ...this.state, openStack: !this.state.openStack })}>Toggle stack trace</a>
          <Collapse isExpanded={this.state.openStack}>
            <pre>{errorDetails.stacktrace}</pre>
          </Collapse>
          <br />
        </div>
      );
    }

    let queryInfo: ReactElement = null;
    if (errorDetails?.request) {
      queryInfo = (
        <div>
          <a onClick={() => this.setState({ ...this.state, openQueryInfo: !this.state.openQueryInfo })}>
            Toggle query info
          </a>
          <Collapse isExpanded={this.state.openQueryInfo}>
            <pre>
              <b>Query / Mutation</b>
              <br />
              {errorDetails.request}

              <b>Variables</b>
              <br />
              {errorDetails.variables}
            </pre>
          </Collapse>
        </div>
      );
    }

    return (
      <li className="error-row">
        <span className="error-date">
          <TimeAgo dateTimeISO={this.props.date} />
        </span>
        <span className="error-item">
          <span className="error-type">{this.props.type}</span>
          <br />
          <span className="error-message">{errorMessage}</span>
          <span className="error-details">
            <div className="error-item-details">
              {stackTrace}
              {queryInfo}
            </div>
          </span>
        </span>
      </li>
    );
  }
}

const ErrorsPage: FC = () => {
  const errorsList: IStampedError[] = useSelector<IAppState, any>((state) =>
    state.errors.errorsList.filter((error) => {
      return !error.type || error.type.startsWith("selector");
    }),
  );

  return (
    <>
      <PageHeader title={"Detected Errors"} />

      <div className={styleLayout.page}>
        <div className={"errors-page"}>
          {errorsList.length === 0 && <ContentPlaceholder text={"No error has been detected yet"} />}
          <ul className="error-rows">
            {errorsList.map((stampedError: IStampedError, index: number) => {
              return <ErrorComponent {...stampedError} key={"error-component-" + index} />;
            })}
          </ul>
        </div>
      </div>
    </>
  );
};

export default ErrorsPage;
